/**
 * A still life of the records stream, for people who have not seen one yet.
 *
 * Hand-built from a fixed list, and deliberately so: the real timeline
 * (`RecordsTimeline`) needs an engine, a loader, a cursor and a signed-in user,
 * none of which a landing page has. Nothing here is computed and nothing is
 * imported from the records stack — it is a picture, drawn in the same tokens as
 * the thing it is a picture of, so what a visitor recognises on the marketing
 * page is what they meet after signing in.
 *
 * `aria-hidden`: the copy beside it says the same thing in words.
 */

/** Where the spine sits, and therefore what every glyph is centred on. */
const SPINE_X = 46;

type Row =
  | { kind: "bucket"; tick: string; label: string }
  | { kind: "gap"; label: string }
  | {
      kind: "record";
      tick?: string;
      title: string;
      preview?: string;
      tags?: string[];
      /** Selected row, the way the real stream marks the record being read. */
      active?: boolean;
      /** Paint a duration band on the spine beside this row. */
      span?: boolean;
    };

const ROWS: Row[] = [
  { kind: "bucket", tick: "20th c.", label: "July 1969" },
  {
    kind: "record",
    tick: "20",
    title: "Apollo 11 lands on the Moon",
    preview:
      "Eagle touches down in the Sea of Tranquility with under a minute of fuel remaining.",
    tags: ["Spaceflight"],
    active: true,
  },
  { kind: "gap", label: "8 centuries" },
  { kind: "bucket", tick: "13th c.", label: "1215" },
  {
    kind: "record",
    tick: "15",
    title: "Magna Carta sealed at Runnymede",
    preview: "Sixty-three clauses, three of which are still on the statute book.",
    tags: ["England", "Law"],
    span: true,
  },
  { kind: "gap", label: "13 centuries" },
  {
    kind: "record",
    tick: "−44",
    title: "The Ides of March",
    tags: ["Rome"],
  },
];

function Tick({ label }: { label: string }) {
  return (
    <div
      className="absolute flex items-center gap-1"
      style={{ right: `calc(100% - ${SPINE_X}px)`, top: 2 }}
    >
      <span className="font-mono text-[11px] leading-none text-ink2 tabular-nums">
        {label}
      </span>
      <span className="h-px w-[6px] shrink-0 bg-node-line" />
    </div>
  );
}

export default function HeroTimeline() {
  return (
    <div
      aria-hidden="true"
      className="relative overflow-hidden rounded-xl border bg-card"
    >
      <div className="relative py-5">
        {/* The spine, continuous behind everything else. */}
        <span
          className="absolute inset-y-0 w-px bg-node-line"
          style={{ left: SPINE_X }}
        />

        {ROWS.map((row, i) => {
          if (row.kind === "bucket") {
            return (
              <div key={i} className="relative py-2 pr-4">
                <Tick label={row.tick} />
                {/* The month diamond, centred on the spine. */}
                <span
                  className="absolute size-[9px] -translate-x-1/2 rotate-45 rounded-[1px] bg-primary"
                  style={{ left: SPINE_X, top: 13 }}
                />
                <div
                  className="flex items-center gap-3"
                  style={{ paddingLeft: SPINE_X + 14 }}
                >
                  <span className="shrink-0 text-[13px] font-semibold text-accent-foreground">
                    {row.label}
                  </span>
                  <span className="h-px grow bg-border" />
                </div>
              </div>
            );
          }

          if (row.kind === "gap") {
            return (
              <div key={i} className="relative py-2">
                {/* The ellipsis carries the card's own background so it reads as
                    a break in the spine rather than a mark laid over it. */}
                <span
                  className="absolute -translate-x-1/2 bg-card px-1 font-mono text-[12px] leading-none text-muted-foreground"
                  style={{ left: SPINE_X, top: 8 }}
                >
                  ⋯
                </span>
                <div
                  className="font-mono text-[10.5px] text-muted-foreground"
                  style={{ paddingLeft: SPINE_X + 12 }}
                >
                  {row.label}
                </div>
              </div>
            );
          }

          return (
            <div
              key={i}
              className={`relative py-2 pr-4 ${
                row.active ? "bg-accent shadow-[inset_3px_0_0_var(--primary)]" : ""
              }`}
            >
              {row.tick && <Tick label={row.tick} />}
              {/* A record with duration paints its extent on the spine. */}
              {row.span && (
                <span
                  className="absolute inset-y-1 w-[3px] -translate-x-1/2 rounded-full bg-primary/55"
                  style={{ left: SPINE_X }}
                />
              )}
              <span
                className={`absolute size-[9px] -translate-x-1/2 rounded-full ${
                  row.active
                    ? "bg-primary"
                    : "border-[1.5px] border-node-line bg-card"
                }`}
                style={{ left: SPINE_X, top: 14 }}
              />
              <div style={{ paddingLeft: SPINE_X + 14 }}>
                <div className="flex items-start justify-between gap-3">
                  <span
                    className="text-[15.5px] font-semibold leading-snug text-foreground"
                    style={{ fontFamily: "var(--font-serif)" }}
                  >
                    {row.title}
                  </span>
                  {row.tags && (
                    <div className="flex shrink-0 gap-1.5 pt-1">
                      {row.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-[4px] border bg-card px-1.5 py-px text-[10.5px] leading-[1.5] text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {row.preview && (
                  <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-ink2">
                    {row.preview}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* The list does not end here, it is only where the picture stops. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
    </div>
  );
}
